<template>
  <div class="gx-detail">
    <gx-loading :show="pageLoading" text="加载中..." />

    <div class="gx-detail__head">
      <button class="gx-detail__back" @click="goBack">〈 返回列表</button>
      <span class="gx-detail__head-title">微店售后详情</span>
    </div>

    <div v-if="loadError" class="gx-detail__error">
      <p>{{ loadError }}</p>
      <button class="gx-detail__btn gx-detail__btn--primary" @click="goBack">返回列表</button>
    </div>

    <div v-else-if="aftersale" class="gx-detail__layout">
      <div class="gx-detail__main">
        <!-- 处理不一致（G4） -->
        <div
          v-if="showDiffDealAlert"
          class="gx-detail__alert gx-detail__alert--warn"
        >
          <p class="gx-detail__alert-title">{{ diffDealStateText }}</p>
          <p class="gx-detail__alert-body">{{ diffDealManualHint }}</p>
        </div>

        <!-- 处理进度 -->
        <div class="gx-detail__card">
          <div class="gx-detail__card-title">
            <span class="gx-detail__card-title-bar"></span>
            处理进度
          </div>
          <div class="gx-detail__steps">
            <div
              v-for="(item, idx) in processStepBarList"
              :key="item.key"
              class="gx-detail__step"
              :class="'gx-detail__step--' + item.uiState"
            >
              <span v-if="idx > 0" class="gx-detail__step-line" :class="{ 'gx-detail__step-line--red': item.uiState !== 'wait' }"></span>
              <div class="gx-detail__step-node">
                <span class="gx-detail__step-icon">
                  <svg v-if="item.uiState === 'error'" width="11" height="11" viewBox="0 0 10 10" fill="none">
                    <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                  </svg>
                  <span v-else>{{ idx + 1 }}</span>
                </span>
                <span class="gx-detail__step-title">{{ item.title }}</span>
              </div>
            </div>
          </div>
          <div v-if="countdownText || canAccept || canReject" class="gx-detail__actions">
            <span v-if="countdownText" class="gx-detail__countdown">
              请在 <span class="gx-detail__countdown-num">{{ countdownText }}</span> 内处理，超时未处理将自动同意
            </span>
            <button
              v-if="canAccept"
              class="gx-detail__btn gx-detail__btn--sm gx-detail__btn--primary"
              :disabled="operateSubmitting"
              @click="bindAccept"
            >{{ operateSubmitting ? '处理中...' : '同意售后' }}</button>
            <button v-if="canReject" class="gx-detail__btn gx-detail__btn--sm" :disabled="operateSubmitting" @click="bindReject">拒绝</button>
          </div>
        </div>

        <!-- 售后信息 -->
        <div class="gx-detail__card">
          <div class="gx-detail__card-title">
            <span class="gx-detail__card-title-bar"></span>
            售后信息
            <span v-if="typeTagText" class="gx-detail__tag gx-detail__tag--type">{{ typeTagText }}</span>
          </div>
          <div class="gx-detail__grid">
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后类型</span>
              <span class="gx-detail__val">{{ typeTagText || '—' }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后状态</span>
              <span class="gx-detail__val">{{ statusText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">微店订单号</span>
              <span class="gx-detail__val">
                {{ aftersale.wx_order_id || '—' }}
                <i v-if="aftersale.wx_order_id" class="gx-detail__copy" @click="copyText(aftersale.wx_order_id)">⧉</i>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">下单时间</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': orderCreateDisplay.missing }">{{ orderCreateDisplay.text }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后编号</span>
              <span class="gx-detail__val">
                {{ aftersale.wx_after_sale_id || '—' }}
                <i v-if="aftersale.wx_after_sale_id" class="gx-detail__copy" @click="copyText(aftersale.wx_after_sale_id)">⧉</i>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">1688售后编号</span>
              <span class="gx-detail__val">
                <template v-if="alibabaOrderRefundId">
                  <a
                    class="gx-detail__link"
                    target="_blank"
                    rel="noopener noreferrer"
                    :href="'https://dispute.1688.com/refund/assureRefundDetail.htm?refundId=' + alibabaOrderRefundId"
                  >{{ alibabaOrderRefundId }}</a>
                  <i class="gx-detail__copy" @click="copyText(String(alibabaOrderRefundId))">⧉</i>
                </template>
                <template v-else>—</template>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">1688售后状态</span>
              <span class="gx-detail__val">{{ alibabaOrderRefundStatusText || '—' }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">申请时间</span>
              <span class="gx-detail__val">{{ applyTimeText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">申请金额</span>
              <span class="gx-detail__val">{{ refundAmountText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">消费者应退款</span>
              <span class="gx-detail__val gx-detail__val--money">{{ consumerRefundText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">收货状态</span>
              <span class="gx-detail__val">{{ receiptStatusText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">申请件数</span>
              <span class="gx-detail__val">{{ aftersaleNumText }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">发货状态</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': shipStatusDisplay.missing }">{{ shipStatusDisplay.text }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">消费者寄回数量</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': returnNumDisplay.missing }">{{ returnNumDisplay.text }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后原因</span>
              <span class="gx-detail__val">{{ reasonText }}</span>
            </div>
            <div v-if="exchangeSpecStr" class="gx-detail__row">
              <span class="gx-detail__label">原卖出规格</span>
              <span class="gx-detail__val">{{ originalSoldSpecStr || '—' }}</span>
            </div>
            <div v-if="exchangeSpecStr" class="gx-detail__row">
              <span class="gx-detail__label">换货规格</span>
              <span class="gx-detail__val gx-detail__val--exchange">{{ exchangeSpecStr }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">订单备注</span>
              <div class="gx-detail__val">
                <i
                  v-if="merchantNotesFlagColor"
                  class="el-icon-s-flag"
                  :style="{ color: merchantNotesFlagColor, 'margin-right': '6px' }"
                ></i>
                <span>{{ merchantRemarkText }}</span>
                <i
                  v-if="canEditOrderRemark"
                  class="el-icon-edit-outline gx-detail__copy"
                  title="修改订单备注"
                  @click="openMerchantNotes"
                ></i>
              </div>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">店铺</span>
              <span class="gx-detail__val">{{ shopName }}</span>
            </div>
          </div>
        </div>

        <!-- 物流信息 -->
        <div class="gx-detail__card">
          <div class="gx-detail__card-hd">
            <div class="gx-detail__card-title">
              <span class="gx-detail__card-title-bar"></span>
              物流信息
            </div>
            <div class="gx-detail__card-tabs">
              <button class="gx-detail__tab" :class="{ 'gx-detail__tab--on': logisticsTab === 'order' }" @click="logisticsTab = 'order'">订单物流</button>
              <button
                v-if="showReturnLogisticsTab"
                class="gx-detail__tab"
                :class="{ 'gx-detail__tab--on': logisticsTab === 'return' }"
                @click="logisticsTab = 'return'"
              >退货物流</button>
            </div>
          </div>
          <div v-if="logisticsTab === 'order'" class="gx-detail__log-block">
            <div class="gx-detail__row">
              <span class="gx-detail__label">快递公司</span>
              <span class="gx-detail__val">{{ orderLogistics.company || '—' }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">物流单号</span>
              <span class="gx-detail__val">
                {{ orderLogistics.no || '—' }}
                <i v-if="orderLogistics.no" class="gx-detail__copy" @click="copyText(orderLogistics.no)">⧉</i>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">发货时间</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': orderLogisticsTimeDisplay.missing }">{{ orderLogisticsTimeDisplay.text }}</span>
            </div>
          </div>
          <div v-else class="gx-detail__log-block">
            <div class="gx-detail__row"><span class="gx-detail__label">物流公司</span><span class="gx-detail__val">{{ returnLogistics.company || '—' }}</span></div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">物流单号</span>
              <span class="gx-detail__val">
                {{ returnLogistics.no || '—' }}
                <i v-if="returnLogistics.no" class="gx-detail__copy" @click="copyText(returnLogistics.no)">⧉</i>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">寄回时间</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': returnLogisticsTimeDisplay.missing }">{{ returnLogisticsTimeDisplay.text }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后地址</span>
              <span class="gx-detail__val" :class="{ 'gx-detail__static-missing': returnAddressDisplay.missing }">{{ returnAddressDisplay.text }}</span>
            </div>
          </div>
        </div>

        <!-- 微店商品信息 -->
        <div class="gx-detail__card">
          <div class="gx-detail__card-title">
            <span class="gx-detail__card-title-bar gx-detail__card-title-bar--blue"></span>
            微店商品信息
          </div>
          <div v-if="productRow">
            <div class="gx-detail__product gx-detail__product--wx-only">
              <div class="gx-detail__pic">
                <span class="gx-detail__pic-img">
                  <img v-if="productPic" :src="productPic" alt="" />
                  <span v-else>🛍️</span>
                </span>
              </div>
              <div class="gx-detail__pbody">
                <div class="gx-detail__ptitle">{{ productRow.product_name || productRow.title || '—' }}</div>
              </div>
            </div>
            <div class="gx-detail__grid">
              <div class="gx-detail__row">
                <span class="gx-detail__label">规格</span>
                <span class="gx-detail__val">{{ productRow.spec_value || productRow.sku_name || '—' }}</span>
              </div>
              <div v-if="exchangeSpecStr" class="gx-detail__row">
                <span class="gx-detail__label">换货规格</span>
                <span class="gx-detail__val gx-detail__val--exchange">{{ exchangeSpecStr }}</span>
              </div>
              <div class="gx-detail__row">
                <span class="gx-detail__label">数量</span>
                <span class="gx-detail__val">{{ productRow.item_num != null ? productRow.item_num : '—' }}</span>
              </div>
              <div class="gx-detail__row">
                <span class="gx-detail__label">商品ID</span>
                <span class="gx-detail__val">{{ productRow.product_id || productRow.goods_id || '—' }}</span>
              </div>
            </div>
            <div class="gx-detail__price">金额明细</div>
            <div class="gx-detail__grid">
              <div class="gx-detail__row">
                <div class="gx-detail__label">退款金额</div>
                <div class="gx-detail__val gx-detail__val--money">{{ refundAmountText }}</div>
              </div>
              <div class="gx-detail__row">
                <div class="gx-detail__label">实付金额</div>
                <div class="gx-detail__val" :class="{ 'gx-detail__static-missing': userPaidDisplay.missing }">{{ userPaidDisplay.text }}</div>
              </div>
            </div>
          </div>
          <p v-else class="gx-detail__ops-empty-tip">暂无商品信息</p>
        </div>
      </div>

      <!-- 右侧栏 -->
      <div class="gx-detail__side">
        <div class="gx-detail__card">
          <div class="gx-detail__card-title">
            <span class="gx-detail__card-title-bar"></span>
            处理时间线
          </div>
          <div v-if="timelineRecordsDisplay.length" class="gx-detail__timeline">
            <div
              v-for="(rec, idx) in timelineRecordsDisplay"
              :key="'neg-' + idx + '-' + (rec.time || '')"
              class="gx-detail__tl-item"
              :class="{
                'gx-detail__tl-item--user': rec.role === 1,
                'gx-detail__tl-item--plain': rec.role !== 1 && !rec.text && !(rec.desc_kvs && rec.desc_kvs.length),
                'gx-detail__tl-item--ok': rec.role !== 1 && /(完成|结束)/.test(rec.action || ''),
                'gx-detail__tl-item--done': rec.role !== 1 && !/(完成|结束)/.test(rec.action || '') && /成功|同意/.test(rec.action || '')
              }"
            >
              <span class="gx-detail__tl-dot"></span>
              <div class="gx-detail__tl-body">
                <div class="gx-detail__tl-action">{{ rec.action || '节点' }}</div>
                <div v-if="rec.time" class="gx-detail__tl-time">{{ rec.time }}</div>
                <div v-if="rec.text" class="gx-detail__tl-text">{{ rec.text }}</div>
                <div v-if="rec.desc_kvs && rec.desc_kvs.length" class="gx-detail__tl-kvs">
                  <p v-for="(kv, ki) in rec.desc_kvs" :key="ki"><em>{{ kv.key }}</em>：{{ kv.value }}</p>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="gx-detail__ops-empty-tip">暂无时间线数据</p>
          <div v-if="negotiationNeedToggle" class="gx-detail__tl-toggle">
            <button class="gx-detail__link" @click="negotiationExpanded = !negotiationExpanded">
              <template v-if="negotiationExpanded">收起 ▲</template>
              <template v-else>展开全部（共 {{ negotiationTotal }} 条） ▼</template>
            </button>
          </div>
        </div>

        <div
          v-for="(ali, aliIdx) in alibabaAftersaleList"
          :key="ali.id || ali.alibaba_order_id || 'ali-' + aliIdx"
          class="gx-detail__card gx-detail__ali-side-card"
        >
          <div class="gx-detail__card-title">
            <span class="gx-detail__card-title-bar gx-detail__card-title-bar--orange"></span>
            1688 售后
            <span
              v-if="aliExchangePurchaseRoleText(ali)"
              class="gx-detail__tag gx-detail__tag--exchange-old"
            >{{ aliExchangePurchaseRoleText(ali) }}</span>
            <span v-if="alibabaAftersaleList.length > 1" class="gx-detail__tag gx-detail__tag--type">组 {{ aliIdx + 1 }}</span>
          </div>
          <div v-if="aliProductVisible(ali)" class="gx-detail__product gx-detail__product--ali-side">
            <img v-if="aliProductImg(ali)" class="gx-detail__product-img" :src="aliProductImg(ali)" alt="" />
            <div class="gx-detail__product-body">
              <p class="gx-detail__product-title">{{ aliProductName(ali) }}</p>
              <p class="gx-detail__product-spec">{{ aliProductSpec(ali) }}</p>
              <p v-if="aliProductOfferId(ali)" class="gx-detail__product-meta">1688 商品ID：{{ aliProductOfferId(ali) }}</p>
            </div>
          </div>
          <div class="gx-detail__log-block">
            <div class="gx-detail__row">
              <span class="gx-detail__label">售后金额</span>
              <span class="gx-detail__val gx-detail__val--money">¥{{ formatAliAmount(ali.refund_amount) }}</span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">上家旺旺</span>
              <span class="gx-detail__val">
                <span class="gx-detail__link" @click="openAlibabaWangwangChat(ali)">{{ aliWangwang(ali) }}</span>
                <i v-if="aliChatable(ali)" class="el-icon-chat-dot-round gx-detail__ww" title="打开旺旺" @click="openAlibabaWangwangChat(ali)" />
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">采购单号</span>
              <span class="gx-detail__val">
                <a
                  v-if="ali.alibaba_order_id"
                  target="_blank"
                  class="gx-detail__link"
                  :href="'https://air.1688.com/app/ctf-page/trade-order-detail/index.html?orderId=' + ali.alibaba_order_id"
                >{{ ali.alibaba_order_id }}</a>
                <template v-else>—</template>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">1688 售后单号</span>
              <span class="gx-detail__val">
                <a
                  v-if="ali.refund_id"
                  target="_blank"
                  class="gx-detail__link"
                  :href="'https://dispute.1688.com/refund/assureRefundDetail.htm?refundId=' + ali.refund_id"
                >{{ ali.refund_id }}</a>
                <template v-else>—</template>
              </span>
            </div>
            <div class="gx-detail__row">
              <span class="gx-detail__label">退款状态</span>
              <span class="gx-detail__val">{{ aliStatusText(ali) }}</span>
            </div>
          </div>
          <div class="gx-detail__ops">
            <div class="gx-detail__ops-hd">退款操作记录</div>
            <div v-if="aliOpsDisplay(aliIdx).length" class="gx-detail__timeline">
              <div
                v-for="(op, oi) in aliOpsDisplay(aliIdx)"
                :key="'ali-op-' + aliIdx + '-' + (op.operation_id || oi)"
                class="gx-detail__tl-item"
              >
                <span class="gx-detail__tl-dot"></span>
                <div class="gx-detail__tl-body">
                  <div class="gx-detail__tl-time">{{ op.gmt_create_text || '—' }}</div>
                  <div class="gx-detail__tl-action">{{ aliRefundOpTitle(op) }}</div>
                  <div v-if="aliRefundOpDiscriptionText(op)" class="gx-detail__tl-text gx-detail__tl-text--pre">{{ aliRefundOpDiscriptionText(op) }}</div>
                  <div v-if="op.after_operate_status" class="gx-detail__tl-kvs">
                    <p><em>状态</em>：{{ op.after_operate_status_text || op.after_operate_status }}</p>
                  </div>
                </div>
              </div>
            </div>
            <p v-else-if="ali.refund_id" class="gx-detail__ops-empty-tip">暂无操作记录（或尚未同步）</p>
            <p v-else class="gx-detail__ops-empty-tip">尚未申请 1688 退款</p>
            <div v-if="aliOpsNeedToggle(aliIdx)" class="gx-detail__tl-toggle">
              <button class="gx-detail__link" @click="toggleAliOps(aliIdx)">
                <template v-if="isAliOpsExpanded(aliIdx)">收起 ▲</template>
                <template v-else>展开全部（共 {{ aliOpsList(ali).length }} 条） ▼</template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <merchantNotesDialog
      :show="remarkDialog.show"
      :order-id="remarkDialog.orderId"
      :shop-id="remarkDialog.shopId"
      :wx-order-id="remarkDialog.wxOrderId"
      :init-notes="remarkDialog.notes"
      :init-tag-color="remarkDialog.tagColor"
      @close="remarkDialog.show = false"
      @complete="onMerchantNotesSaved"
    />
  </div>
</template>

<script>
import GxLoading from '../../components/newUi/GxLoading.vue'
import merchantNotesDialog from '../xdOrderList/components/merchant_notes/index.vue'
import { getAftersaleDetail, postAftersaleAccept, postAftersaleReject } from '../../api/wxshop/aftersale'

const WX_FINISHED = [
  'MERCHANT_REFUND_SUCCESS',
  'MERCHANT_RETURN_SUCCESS',
  'MERCHANT_EXCHANGE_SUCCESS',
  'MERCHANT_OVERDUE_REFUND'
]

const PLACEHOLDER = {
  orderTime: '2026-04-26 12:36:42',
  returnShipTime: '待微信同步寄回时间',
  returnAddress: '待接口返回退货地址',
  shipStatus: '待同步发货状态',
  returnNum: '1 件',
  userPaid: '¥99.00'
}

const NEGOTIATION_PREVIEW_LEN = 3
const ALI_OPS_PREVIEW_LEN = 3

export default {
  name: 'WxAfterSalesDetailPage',
  components: { GxLoading, merchantNotesDialog },
  props: {
    aftersaleId: { type: [Number, String], default: '' }
  },
  data() {
    return {
      pageLoading: false,
      loadError: '',
      aftersale: null,
      operateSubmitting: false,
      logisticsTab: 'order',
      negotiationExpanded: false,
      aliOpsExpanded: {},
      countdownTimer: null,
      countdownText: '',
      remarkDialog: {
        show: false,
        orderId: 0,
        shopId: '',
        wxOrderId: '',
        notes: '',
        tagColor: 0
      }
    }
  },
  computed: {
    afterId() {
      if (this.aftersaleId !== '' && this.aftersaleId != null) {
        const id = parseInt(this.aftersaleId, 10)
        return Number.isFinite(id) ? id : 0
      }
      const q = this.$route.query.id
      const id = parseInt(q, 10)
      return Number.isFinite(id) ? id : 0
    },
    textPart() {
      const tp = this.aftersale && this.aftersale.text_part
      return tp && typeof tp === 'object' && !Array.isArray(tp) ? tp : {}
    },
    typeTagText() {
      return this.textPart.aftersale_type_text || this.aftersale?.aftersale_type_text || ''
    },
    statusText() {
      return this.textPart.aftersale_status_text || this.aftersale?.aftersale_status_text || '—'
    },
    reasonText() {
      return this.textPart.reason_text || this.aftersale?.reason_text || '—'
    },
    applyTimeText() {
      return this.aftersale?.apply_time || this.aftersale?.wx_apply_time || '—'
    },
    refundAmountText() {
      const v = this.aftersale?.refund_amount
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (!Number.isFinite(n)) return '¥' + v
      return '¥' + (n / 100).toFixed(2)
    },
    consumerRefundText() {
      const eff = Number(this.aftersale?.effective_refund_amount)
      if (Number.isFinite(eff) && eff >= 0) {
        return '¥' + (eff / 100).toFixed(2)
      }
      return this.refundAmountText
    },
    shopName() {
      const s = this.aftersale?.shop
      return (s && s.shop_name) || this.aftersale?.shop_name || '—'
    },
    productRow() {
      return (this.aftersale && this.aftersale.order_product) || null
    },
    productPic() {
      if (!this.productRow) return ''
      return this.productRow.product_pic || this.productRow.thumb_img || ''
    },
    exchangeSpecStr() {
      const d = this.aftersale
      if (!d) return ''
      return d.exchange_product_spec_str
        || (d.order_product && d.order_product.exchange_product_spec_str)
        || ''
    },
    originalSoldSpecStr() {
      const p = this.productRow
      if (!p) return ''
      return p.spec_value || p.sku_name || ''
    },
    showDiffDealAlert() {
      const st = Number(this.aftersale && this.aftersale.diff_deal_state)
      return st === 10 || st === 20
    },
    diffDealStateText() {
      const d = this.aftersale
      if (!d) return ''
      return d.diff_deal_state_text || '处理不一致'
    },
    diffDealManualHint() {
      if (!this.showDiffDealAlert) return ''
      const list = this.alibabaAftersaleList
      const parts = []
      list.forEach((ali) => {
        const rm = String(ali.step_remark || '').trim()
        if (rm) parts.push(rm)
      })
      if (parts.length) {
        return '1688说明：' + parts.join('；') + '。请人工核对微信与1688结论，勿自动关单。'
      }
      return '微信与1688处理结论不一致，请人工核对后再操作。'
    },
    alibabaAftersaleList() {
      const d = this.aftersale
      if (!d) return []
      if (Array.isArray(d.alibaba_aftersale_list) && d.alibaba_aftersale_list.length) {
        return d.alibaba_aftersale_list
      }
      if (d.alibaba_aftersale) return [d.alibaba_aftersale]
      return []
    },
    alibabaOrderRefundId() {
      return String(this.aftersale?.alibaba_order_refund_id || this.alibabaAftersaleList[0]?.refund_id || '').trim()
    },
    alibabaOrderRefundStatusText() {
      return (
        this.aftersale?.alibaba_order_refund_status_text ||
        this.alibabaAftersaleList[0]?.refund_status_text ||
        ''
      )
    },
    orderCreateDisplay() {
      return this.fieldDisplay(this.aftersale?.order_create_time, PLACEHOLDER.orderTime)
    },
    shipStatusDisplay() {
      return this.fieldDisplay(this.aftersale?.ship_status_text, PLACEHOLDER.shipStatus)
    },
    receiptStatusText() {
      return this.aftersale?.receipt_status_text || '—'
    },
    aftersaleNumText() {
      const n = Number(this.aftersale?.aftersale_num)
      if (Number.isFinite(n) && n > 0) return n + ' 件'
      return '—'
    },
    returnNumDisplay() {
      const n = Number(this.aftersale?.aftersale_num)
      if (Number.isFinite(n) && n > 0) {
        return { text: n + ' 件', missing: false }
      }
      return { text: PLACEHOLDER.returnNum, missing: true }
    },
    userPaidDisplay() {
      const pay = Number(this.productRow?.pay_amount)
      if (Number.isFinite(pay) && pay > 0) {
        return { text: '¥' + (pay / 100).toFixed(2), missing: false }
      }
      return { text: PLACEHOLDER.userPaid, missing: true }
    },
    merchantRemarkText() {
      const notes = this.aftersale?.merchant_remark || this.aftersale?.merchant_notes
      if (notes == null || String(notes).trim() === '') return '暂无'
      return String(notes)
    },
    merchantNotesFlagColor() {
      const info = this.aftersale?.merchant_notes_tag_color_info
      if (info && info.color) return info.color
      return this.aftersale?.merchant_notes_tag_color || ''
    },
    canEditOrderRemark() {
      return !!(this.aftersale && (this.aftersale.order_id || this.aftersale.wx_order_id))
    },
    orderLogistics() {
      const o = this.aftersale?.order_logistics
      if (o && typeof o === 'object') {
        return {
          company: o.company || '',
          no: o.no || '',
          time: o.time || ''
        }
      }
      return { company: '', no: '', time: '' }
    },
    returnLogistics() {
      const o = this.aftersale?.return_logistics
      if (o && typeof o === 'object') {
        return {
          company: o.company || this.aftersale?.return_logistics_company || '',
          no: o.no || this.aftersale?.return_logistics_code || '',
          time: o.time || ''
        }
      }
      return {
        company: this.aftersale?.return_logistics_company || '',
        no: this.aftersale?.return_logistics_code || '',
        time: ''
      }
    },
    orderLogisticsTimeDisplay() {
      if (this.orderLogistics.time) {
        return { text: this.orderLogistics.time, missing: false }
      }
      if (this.orderLogistics.no) {
        return this.fieldDisplay('', PLACEHOLDER.returnShipTime)
      }
      return { text: '—', missing: false }
    },
    returnLogisticsTimeDisplay() {
      return this.fieldDisplay(this.returnLogistics.time, PLACEHOLDER.returnShipTime)
    },
    returnAddressDisplay() {
      return this.fieldDisplay(this.aftersale?.return_address_text, PLACEHOLDER.returnAddress)
    },
    showReturnLogisticsTab() {
      const t = String(this.aftersale?.aftersale_type || '').toUpperCase()
      return t === 'RETURN' || t === 'EXCHANGE' || !!this.returnLogistics.no
    },
    recordLogs() {
      const r = this.aftersale?.record_logs_list || this.aftersale?.wx_timeline
      return Array.isArray(r) ? r : []
    },
    timelineRecords() {
      return this.recordLogs
    },
    negotiationTotal() {
      return this.timelineRecords.length
    },
    negotiationNeedToggle() {
      return this.negotiationTotal > NEGOTIATION_PREVIEW_LEN
    },
    timelineRecordsDisplay() {
      const list = this.timelineRecords
      if (!list.length) return []
      if (this.negotiationExpanded || list.length <= NEGOTIATION_PREVIEW_LEN) return list
      return list.slice(-NEGOTIATION_PREVIEW_LEN)
    },
    processStepBarList() {
      const type = String(this.aftersale?.aftersale_type || '').toUpperCase()
      const st = String(this.aftersale?.aftersale_status || '').toUpperCase()
      const finished = WX_FINISHED.includes(st)
      const rejected = st.startsWith('MERCHANT_REJECT') || st === 'USER_CANCEL' || st === 'USER_CANCELD'
      let titles = ['用户申请', '商家处理', '售后完成']
      let active = 1
      if (type === 'REFUND') {
        titles = ['用户申请售后', '商家处理', '退款完成']
        if (finished) active = 3
        else if (st === 'MERCHANT_PROCESSING') active = 1
        else active = 2
      } else if (type === 'RETURN') {
        titles = ['用户申请', '商家同意', '买家寄回', '商家收货', '售后完成']
        if (finished) active = 5
        else if (st === 'MERCHANT_PROCESSING') active = 1
        else if (st === 'USER_WAIT_RETURN') active = 2
        else if (st === 'MERCHANT_WAIT_RECEIPT') active = 3
        else if (st === 'MERCHANT_WAIT_REFUND' || st === 'PLATFORM_REFUNDING') active = 4
        else active = 1
      } else if (type === 'EXCHANGE') {
        titles = ['用户申请', '商家处理', '买家寄回', '商家发货', '换货完成']
        if (finished) active = 5
        else if (st === 'MERCHANT_PROCESSING') active = 1
        else if (st === 'USER_WAIT_RETURN') active = 2
        else if (st === 'MERCHANT_WAIT_RECEIPT') active = 3
        else if (st === 'USER_WAIT_RECEIPT' || st === 'PLATFORM_REFUNDING') active = 4
        else active = 1
      }
      return titles.map((title, idx) => {
        let uiState = 'wait'
        if (finished) uiState = 'done'
        else if (idx < active) uiState = 'done'
        else if (idx === active) uiState = rejected ? 'error' : 'current'
        return { key: 'wx-step-' + idx, title, uiState }
      })
    },
    canAccept() {
      return !!(this.aftersale && this.aftersale.can_accept)
    },
    canReject() {
      return !!(this.aftersale && this.aftersale.can_reject)
    }
  },
  watch: {
    '$route.query.id'() {
      this.loadDetail()
    },
    aftersale() {
      this.resetCountdown()
    }
  },
  created() {
    this.loadDetail()
  },
  beforeDestroy() {
    this.clearCountdown()
  },
  methods: {
    fieldDisplay(val, staticText) {
      const s = val != null ? String(val).trim() : ''
      if (s && s !== '—' && s !== '-') {
        return { text: s, missing: false }
      }
      if (staticText) {
        return { text: staticText, missing: true }
      }
      return { text: '—', missing: false }
    },
    aliOpsList(ali) {
      const ops = ali && ali.alibaba_refund_operation_list
      return Array.isArray(ops) ? ops : []
    },
    aliOpsDisplay(aliIdx) {
      const ali = this.alibabaAftersaleList[aliIdx]
      const list = this.aliOpsList(ali)
      if (!list.length) return []
      if (this.isAliOpsExpanded(aliIdx) || list.length <= ALI_OPS_PREVIEW_LEN) return list
      return list.slice(-ALI_OPS_PREVIEW_LEN)
    },
    aliOpsNeedToggle(aliIdx) {
      return this.aliOpsList(this.alibabaAftersaleList[aliIdx]).length > ALI_OPS_PREVIEW_LEN
    },
    isAliOpsExpanded(aliIdx) {
      return !!this.aliOpsExpanded[aliIdx]
    },
    toggleAliOps(aliIdx) {
      this.$set(this.aliOpsExpanded, aliIdx, !this.isAliOpsExpanded(aliIdx))
    },
    aliStatusText(ali) {
      if (!ali) return '—'
      return ali.refund_status_text || ali.status_text || ali.refund_status || '—'
    },
    formatAliAmount(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0.00'
      return n.toFixed(2)
    },
    /** 1688 操作记录 discription：接口用 | 分隔，展示时换行 */
    aliRefundOpDiscriptionText(op) {
      const raw = String(op?.discription ?? '').trim()
      if (!raw) return ''
      const remark = String(op?.operate_remark ?? '').trim()
      if (remark !== '' && remark === raw) return ''
      const parts = raw
        .split('|')
        .map((s) => s.trim())
        .filter((s) => s !== '')
      if (parts.length === 0) return ''
      if (parts.length === 1 && remark === '') return ''
      return parts.join('\n')
    },
    aliRefundOpTitle(op) {
      const remark = String(op?.operate_remark ?? '').trim()
      if (remark) return remark
      const raw = String(op?.discription ?? '').trim()
      const parts = raw
        .split('|')
        .map((s) => s.trim())
        .filter((s) => s !== '')
      if (parts.length === 1) return parts[0]
      if (parts.length > 1) return '操作记录'
      return '操作记录'
    },
    aliExchangePurchaseRoleText(ali) {
      if (!ali) return ''
      if (ali.exchange_purchase_role === 'old') {
        return ali.exchange_purchase_role_text || '旧采购单'
      }
      return ''
    },
    aliProductVisible(ali) {
      const p = ali && ali.product
      if (!p) return false
      return !!(p.img || p.name || p.sku_spec_value || p.offer_id)
    },
    aliProductImg(ali) {
      const p = ali && ali.product
      return (p && p.img) || ''
    },
    aliProductName(ali) {
      const p = ali && ali.product
      return (p && p.name) || '—'
    },
    aliProductSpec(ali) {
      const p = ali && ali.product
      return (p && p.sku_spec_value) || '—'
    },
    aliProductOfferId(ali) {
      const p = ali && ali.product
      return (p && p.offer_id) || ''
    },
    aliWangwang(ali) {
      return (ali && ali.wangwang_account) || '—'
    },
    aliChatable(ali) {
      if (!ali) return false
      return !!(ali.wangwang_account || ali.wangwang_chat_url)
    },
    openAlibabaWangwangChat(ali) {
      const row = ali || {}
      const url = String(row.wangwang_chat_url || '').trim()
      if (url) {
        window.open(url, '_blank')
        return
      }
      const account = String(row.wangwang_account || '').trim()
      if (!account) {
        this.$message({ message: '暂无上家旺旺', type: 'warning' })
        return
      }
      const wangwangId = account.replace(/^cnalichn/i, '')
      const touid = encodeURIComponent('cnalichn' + wangwangId)
      window.open(
        `https://air.1688.com/app/ocms-fusion-components-1688/def_cbu_web_im/index.html?&touid=${touid}&siteid=cnalichn&status=1&portalId=&gid=&offerId=&itemsId=&spmid=a261y.7663282.0#/`,
        '_blank'
      )
    },
    resetCountdown() {
      this.clearCountdown()
      const dl = Number(this.aftersale?.deadline)
      if (!Number.isFinite(dl) || dl <= 0) {
        this.countdownText = ''
        return
      }
      const tick = () => {
        const now = Math.floor(Date.now() / 1000)
        let ts = dl
        if (ts > 1e12) ts = Math.floor(ts / 1000)
        const remain = ts - now
        if (remain <= 0) {
          this.countdownText = '00:00:00'
          this.clearCountdown()
          return
        }
        const h = Math.floor(remain / 3600)
        const m = Math.floor((remain % 3600) / 60)
        const s = remain % 60
        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        this.countdownText = pad(h) + ':' + pad(m) + ':' + pad(s)
      }
      tick()
      this.countdownTimer = setInterval(tick, 1000)
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    openMerchantNotes() {
      const item = this.aftersale
      if (!item) return
      const shop = item.shop || {}
      this.remarkDialog = {
        show: true,
        orderId: Number(item.order_id) || 0,
        shopId: shop.shop_id || item.shop_id || '',
        wxOrderId: item.wx_order_id || '',
        notes: item.merchant_remark || item.merchant_notes || '',
        tagColor: item.merchant_notes_tag_color != null ? item.merchant_notes_tag_color : 0
      }
    },
    onMerchantNotesSaved(data) {
      if (!this.aftersale || !data) return
      const notes = data.merchant_notes != null ? data.merchant_notes : data.merchant_remark
      this.$set(this.aftersale, 'merchant_remark', notes || null)
      this.$set(this.aftersale, 'merchant_notes', notes || null)
      this.$set(this.aftersale, 'merchant_notes_tag_color', data.merchant_notes_tag_color)
      this.$set(this.aftersale, 'merchant_notes_tag_color_info', data.merchant_notes_tag_color_info || null)
    },
    loadDetail() {
      if (!this.afterId) {
        this.loadError = '缺少售后单 ID'
        return
      }
      this.pageLoading = true
      this.loadError = ''
      getAftersaleDetail({ id: this.afterId })
        .then((res) => {
          this.pageLoading = false
          if (res.code !== 200) {
            this.loadError = res.message || res.msg || '加载失败'
            this.aftersale = null
            return
          }
          this.aftersale = res.data || null
          if (!this.aftersale) this.loadError = '未找到售后记录'
        })
        .catch(() => {
          this.pageLoading = false
          this.loadError = '售后详情接口未就绪，请确认后端服务与数据表已部署'
          this.aftersale = null
        })
    },
    goBack() {
      this.$router.replace({ path: '/new_aftersales_workbench', query: { menu: 'wx-after-mag' } }).catch(() => {})
    },
    copyText(text) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('Copy')
      document.body.removeChild(input)
      this.$message({ message: '复制成功', type: 'success' })
    },
    bindAccept() {
      if (!this.aftersale?.id || this.operateSubmitting) return
      const exchangeHint = this.aftersale.exchange_second_accept_hint
      if (exchangeHint) {
        this.$alert(exchangeHint, '提示', { confirmButtonText: '知道了', type: 'info' })
        return
      }
      this.$confirm('确认同意该售后申请？', '提示', { type: 'warning' })
        .then(() => {
          this.operateSubmitting = true
          postAftersaleAccept({ id: this.aftersale.id })
            .then((res) => {
              this.operateSubmitting = false
              if (res.code !== 200) {
                this.$message.error(res.message || res.msg || '操作失败')
                return
              }
              this.$message.success('操作成功')
              this.loadDetail()
            })
            .catch(() => {
              this.operateSubmitting = false
              this.$message.error('操作失败')
            })
        })
        .catch(() => {})
    },
    bindReject() {
      if (!this.aftersale?.id || this.operateSubmitting) return
      this.$prompt('请输入拒绝原因', '拒绝售后', {
        inputPattern: /\S+/,
        inputErrorMessage: '请填写拒绝原因'
      })
        .then(({ value }) => {
          this.operateSubmitting = true
          postAftersaleReject({ id: this.aftersale.id, reason: value })
            .then((res) => {
              this.operateSubmitting = false
              if (res.code !== 200) {
                this.$message.error(res.message || res.msg || '操作失败')
                return
              }
              this.$message.success('已拒绝')
              this.loadDetail()
            })
            .catch(() => {
              this.operateSubmitting = false
              this.$message.error('操作失败')
            })
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../styles/gx-after-sales-detail.scss';

.gx-detail__ali-side-card + .gx-detail__ali-side-card {
  margin-top: 12px;
}
.gx-detail__product--wx-only {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.gx-detail__product--ali-side {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f2f5;
}
.gx-detail__product-img {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #e1e5ee;
  flex-shrink: 0;
}
.gx-detail__product-body {
  min-width: 0;
}
.gx-detail__product-title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.4;
}
.gx-detail__product-spec,
.gx-detail__product-meta {
  margin: 0 0 2px;
  font-size: 12px;
  color: #6a6a8a;
}
.gx-detail__tl-text--pre {
  white-space: pre-line;
  margin-top: 4px;
}
.gx-detail__val--money {
  color: #c8443c;
  font-weight: 700;
}
.gx-detail__val--exchange {
  color: #1677ff;
}
.gx-detail__alert {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid #faecd8;
  background: #fdf6ec;
}
.gx-detail__alert--warn {
  border-color: #f5dab1;
}
.gx-detail__alert-title {
  margin: 0 0 6px;
  font-weight: 600;
  color: #e6a23c;
}
.gx-detail__alert-body {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}
.gx-detail__tag--exchange-old {
  margin-left: 6px;
  background: #fff7e6;
  color: #d46b08;
  border: 1px solid #ffd591;
}
</style>
